//@ts-strict-ignore
import { useQuery } from '@apollo/client';
import productConfigQuery from '../graphql/queries/product/ProductConfigQuery.graphql';
import customerConfigQuery from '../graphql/queries/customer/CustomerConfigQuery.graphql';
import nominalConfigQuery from '../graphql/queries/nominal/NominalConfigQuery.graphql';
import salesOrderConfigQuery from '../graphql/queries/sales/SalesOrderConfigQuery.graphql';
import purchaseOrderConfigQuery from '../graphql/queries/purchases/PurchaseConfigQuery.graphql';
import supplierConfigQuery from '../graphql/queries/supplier/SupplierConfigQuery.graphql';
import bankConfigQuery from '../graphql/queries/bank/BankConfigQuery.graphql';
import companyQuery from '../graphql/queries/company/CompanyQuery.graphql';
import documentConfigQuery from '../graphql/queries/sales/DocumentConfigQuery.graphql';
import NominalCommonAccountSet from '../graphql/entities/nominal/NominalCommonAccountSet';
import componentQuery from '../utils/componentQuery';
import {
  IBankConfig,
  IBankConfigQueryResult,
  IBankConfigQueryVariables,
  ICompany,
  ICompanyQueryResult,
  ICustomerConfigQueryResult,
  IDocumentConfig,
  IDocumentConfigQueryResult,
  INominalCommonAccountSetsQueryResult,
  INominalConfigQueryResult,
  IOrderConfig,
  IOrderConfigQueryResult,
  IProductConfig,
  IProductConfigQueryResult,
  IPurchaseConfig,
  IPurchaseConfigQueryResult,
  ISupplierConfigQueryResult,
  Maybe,
} from '../graphql/schema/p4types';
import { useComponentQueryErrorHandling } from './parseErrorResponse';

/**
 * Helper hooks to obtain single-instance configuration records.
 * @returns {*} - Returns configuration entity - NOTE: While loading, will return null/undefined.
 */
export function useProductConfig() {
  const handleQueryError = useComponentQueryErrorHandling();
  const { data } = useQuery<IProductConfigQueryResult>(productConfigQuery, { onError: handleQueryError });
  return data?.data as Maybe<IProductConfig>;
}

export function useCustomerConfig() {
  const handleQueryError = useComponentQueryErrorHandling();
  const { data } = useQuery<ICustomerConfigQueryResult>(customerConfigQuery, { onError: handleQueryError });
  return data?.data;
}

export function useSupplierConfig() {
  const handleQueryError = useComponentQueryErrorHandling();
  const { data } = useQuery<ISupplierConfigQueryResult>(supplierConfigQuery, { onError: handleQueryError });
  return data?.data;
}

export function useSalesOrderConfig() {
  const handleQueryError = useComponentQueryErrorHandling();
  const { data } = useQuery<IOrderConfigQueryResult>(salesOrderConfigQuery, { onError: handleQueryError });
  return data?.data as Maybe<IOrderConfig>;
}

export function usePurchaseOrderConfig() {
  const handleQueryError = useComponentQueryErrorHandling();
  const { data } = useQuery<IPurchaseConfigQueryResult>(purchaseOrderConfigQuery, { onError: handleQueryError });
  return data?.data as Maybe<IPurchaseConfig>;
}

export function useNominalCommonAccountSets() {
  const handleQueryError = useComponentQueryErrorHandling();
  const { data } = useQuery<INominalCommonAccountSetsQueryResult>(NominalCommonAccountSet.workspace, {
    onError: handleQueryError,
  });
  return data?.listData?.edges?.map((edge) => edge?.node);
}

export function useDocumentConfig() {
  const handleQueryError = useComponentQueryErrorHandling();
  const { data } = useQuery<IDocumentConfigQueryResult>(documentConfigQuery, {
    fetchPolicy: 'network-only',
    onError: handleQueryError,
  });
  return data?.data as Maybe<IDocumentConfig>;
}

export function useCompany() {
  const handleQueryError = useComponentQueryErrorHandling();
  const { data } = useQuery<ICompanyQueryResult>(companyQuery, { onError: handleQueryError });
  return data?.data as Maybe<ICompany>;
}

export function useNominalConfig() {
  const handleQueryError = useComponentQueryErrorHandling();
  const { data } = useQuery<INominalConfigQueryResult>(nominalConfigQuery, { onError: handleQueryError });
  return data?.data;
}

/*Consider the below 'fetch' calls as deprecated - Use the hooks above where possible*/
export function fetchProductConfig() {
  return fetchConfig(productConfigQuery);
}

export function fetchBankConfig() {
  return fetchConfig(bankConfigQuery);
}

export function useBankConfig() {
  const handleQueryError = useComponentQueryErrorHandling();
  const { data } = useQuery<IBankConfigQueryResult, IBankConfigQueryVariables>(bankConfigQuery, {
    onError: handleQueryError,
  });
  return data?.data as Maybe<IBankConfig>;
}

async function fetchConfig(query) {
  let config = null;
  try {
    await componentQuery(query, null, true, 'network-only').then(({ response: result }) => {
      config = result && result.data ? result.data : null;
    });
  } catch (e) {
    console.error('Configuration could not be read', e);
  }
  return config;
}
